import { ChangeEvent, FormEvent, KeyboardEvent as ReactKeyboardEvent, useRef, FocusEvent, ReactNode } from "react";
import { Box, ClickAwayListener, IconButton, InputAdornment, OutlinedInput, SxProps, Theme } from "@mui/material";
import { useTranslation } from "next-i18next";
import { IconClose } from "src/components/icons-mui/close";
import { IconSearch } from "src/components/icons-mui/search";
import { SEARCH_RESULTS_LISTBOX_ID } from "src/components-mui/common/typeahead/typeahead.constants";

const CLEAR_BUTTON_TEST_ID = "clear-button";

interface ITypeaheadUI {
    /** The current query entered in the search input */
    query?: string;

    /** Ghost text to show while nothing is entered in the search box */
    placeholderText?: string;

    /**  Enables autofocus on search input on first render, is disabled by default */
    enableAutoFocus?: boolean;

    /**  Enables/disables search input */
    isDisabled?: boolean;

    /** Callback for when the search input changes */
    onChange: (queryValue: string) => unknown;

    /** Callback pressing clear button  */
    onClear: () => void;

    /** Callback for when the typeahead gains keyboard focus */
    onFocus?: (event: FormEvent<EventTarget>) => unknown;

    /** Callback for when the typeahead is manually dismissed */
    onClose?: () => unknown;

    /** Callback for when a key is pressed */
    onKeyDown?: (event: ReactKeyboardEvent<EventTarget>) => unknown;

    /** Callback for when the search query is submitted */
    onSubmit?: (event: FormEvent<EventTarget>) => unknown;

    /**  Search results will come as children */
    children: ReactNode;

    sx?: SxProps<Theme>;
}

export const TypeaheadUI = ({
    query,
    placeholderText,
    enableAutoFocus = false,
    onChange,
    onClear,
    onFocus,
    onClose,
    onKeyDown,
    onSubmit,
    children,
    isDisabled,
    sx,
}: ITypeaheadUI) => {
    const { t } = useTranslation("common");
    const inputRef = useRef<HTMLInputElement>(null);

    const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => onChange(event.currentTarget.value);

    const handleOnClear = () => {
        inputRef.current?.focus();
        onClear();
    };

    return (
        <ClickAwayListener onClickAway={() => onClose?.()} mouseEvent="onPointerDown" touchEvent="onTouchStart">
            <Box role="search" sx={{ position: "relative", ...sx }}>
                <Box component="form" onSubmit={onSubmit} onFocus={onFocus} onInvalid={(evt: FormEvent) => evt.preventDefault()}>
                    <OutlinedInput
                        fullWidth
                        autoFocus={enableAutoFocus}
                        sx={{ paddingLeft: 2, "& .MuiOutlinedInput-input": { paddingLeft: "9px", textOverflow: "ellipsis" } }}
                        startAdornment={
                            <InputAdornment position="start" sx={{ pointerEvents: "none" }}>
                                <IconSearch fontSize="medium" sx={{ color: "gray.800" }} />
                            </InputAdornment>
                        }
                        endAdornment={
                            Boolean(query?.length) && (
                                <InputAdornment position="end" sx={{ marginRight: 1.75 }}>
                                    <IconButton
                                        data-test-id={CLEAR_BUTTON_TEST_ID}
                                        aria-label={t("clear")}
                                        onClick={handleOnClear}
                                        onFocus={(event: FocusEvent) => {
                                            event.stopPropagation();
                                        }}
                                        size="small"
                                        variant="ghost"
                                        color="gray"
                                        sx={{ borderRadius: "50%" }}
                                    >
                                        <IconClose fontSize="medium" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }
                        placeholder={placeholderText}
                        inputRef={inputRef}
                        value={query}
                        onChange={handleOnChange}
                        onKeyDown={onKeyDown}
                        disabled={isDisabled}
                        inputProps={{
                            autoComplete: "off",
                            autoCorrect: "off",
                            autoCapitalize: "off",
                            spellCheck: "false",
                            required: true,
                            title: t("search"),
                            "aria-label": t("search"),
                            role: "combobox",
                            "aria-expanded": Boolean(query?.length),
                            "aria-autocomplete": "list",
                            "aria-controls": SEARCH_RESULTS_LISTBOX_ID,
                        }}
                    />
                </Box>
                {children}
            </Box>
        </ClickAwayListener>
    );
};
